import { useState, useEffect, useCallback } from 'react';
import api from '../../hooks/useApi';
import { useApp } from '../../context';
import { useToast } from '../ui/Toast';

// The client ordering portal's queue. Orders submitted by clients land here;
// the team accepts them, raises the distributor PO (which marks the order
// ordered and emails the client), and marks delivery. Prices shown are the
// CLIENT's prices; the PO raised from here carries our cost instead.

const STATUS_PILL = {
  submitted: 'bg-blue-50 text-blue-700 border-blue-200',
  accepted: 'bg-amber-50 text-amber-700 border-amber-200',
  ordered: 'bg-violet-50 text-violet-700 border-violet-200',
  delivered: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  cancelled: 'bg-gray-50 text-gray-500 border-gray-200',
};

const FILTERS = ['all', 'submitted', 'accepted', 'ordered', 'delivered', 'cancelled'];

const pounds = (pence) => `£${((pence ?? 0) / 100).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function ClientOrdersPage() {
  const { navigate } = useApp();
  const { toast } = useToast();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [expanded, setExpanded] = useState(null);
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get('client-orders').then(r => setOrders(r.orders || [])).catch(() => setOrders([])).finally(() => setLoading(false));
  }, []);
  useEffect(load, [load]);

  const open = async (id) => {
    if (expanded === id) { setExpanded(null); setDetail(null); return; }
    setExpanded(id); setDetail(null);
    try { setDetail((await api.get(`client-orders/${id}`)).order); } catch { setDetail(null); }
  };

  const act = async (fn, okMsg) => {
    setBusy(true);
    try {
      const res = await fn();
      toast.success(typeof okMsg === 'function' ? okMsg(res) : okMsg);
      load();
      if (expanded) setDetail((await api.get(`client-orders/${expanded}`)).order);
    } catch (e) {
      toast.error(e.data?.error || e.message || 'That did not work.');
    } finally { setBusy(false); }
  };

  const setStatus = (id, status, msg) => act(() => api.put(`client-orders/${id}/status`, { status }), msg);
  const raisePo = (id) => act(() => api.post(`client-orders/${id}/create-po`, {}), (r) => `Draft ${r.po_number} raised and the client notified`);

  const visible = filter === 'all' ? orders : orders.filter(o => o.status === filter);
  const count = (s) => orders.filter(o => o.status === s).length;

  return (
    <div className="fade-in">
      <div className="mb-3">
        <h2 className="text-base font-extrabold text-navy">Client Orders</h2>
        <div className="text-[9px] text-gray-500">
          Orders submitted on the client ordering portal. Accept, raise the supplier PO, and mark delivery; the client sees each status change and is emailed.
        </div>
      </div>

      <div className="flex gap-1.5 mb-3 flex-wrap">
        {FILTERS.map(f => (
          <button key={f} onClick={() => setFilter(f)}
            className={`border rounded-full px-3 py-1 text-[9px] font-bold uppercase tracking-wider ${filter === f ? 'bg-navy border-navy text-white' : 'bg-white border-gray-200 text-gray-500 hover:border-gray-300'}`}>
            {f}{f !== 'all' && count(f) > 0 ? ` (${count(f)})` : ''}
          </button>
        ))}
      </div>

      <div className="bg-white border border-gray-200 rounded overflow-hidden">
        <div className="flex items-center gap-3 px-3 py-1.5 bg-gray-50 border-b border-gray-100 text-[8px] uppercase text-gray-500 font-bold">
          <div className="w-32">Reference</div>
          <div className="flex-1">Client / Contact</div>
          <div className="w-20 text-right">Items</div>
          <div className="w-24 text-right">Total inc VAT</div>
          <div className="w-24 text-right">Submitted</div>
          <div className="w-20 text-center">Status</div>
          <div className="w-4" />
        </div>
        {visible.map(o => (
          <div key={o.id} className="border-b border-gray-50 last:border-0">
            <button onClick={() => open(o.id)} className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-gray-50">
              <div className="w-32">
                <div className="text-[11px] font-bold text-navy">{o.reference}</div>
                {o.client_reference && <div className="text-[8px] text-gray-400">{o.client_reference}</div>}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] font-semibold text-gray-700 truncate">{o.client_name}</div>
                <div className="text-[8px] text-gray-400">{o.contact_name}</div>
              </div>
              <div className="w-20 text-right text-[10px] text-gray-500">{o.line_count}</div>
              <div className="w-24 text-right text-[11px] font-bold text-navy">{pounds(o.total_pence)}</div>
              <div className="w-24 text-right text-[9px] text-gray-400">{(o.submitted_at || '').slice(0, 10)}</div>
              <div className="w-20 text-center">
                <span className={`inline-block border rounded-full px-2 py-0.5 text-[8px] font-bold ${STATUS_PILL[o.status] || ''}`}>{o.status}</span>
              </div>
              <span className="w-4 text-gray-400 text-[9px]">{expanded === o.id ? '▲' : '▼'}</span>
            </button>

            {expanded === o.id && detail && (
              <div className="px-3 pb-3 pt-1 bg-gray-50/60">
                <div className="bg-white border border-gray-200 rounded p-3">
                  {detail.lines.map(l => (
                    <div key={l.id} className="flex justify-between text-[10px] text-gray-600 py-1 border-b border-gray-50 last:border-0">
                      <span>
                        {l.quantity} × {l.display_name}
                        <span className="text-gray-400"> · {l.sku}</span>
                        {l.note && <span className="text-amber-700"> — “{l.note}”</span>}
                      </span>
                      <span className="font-bold text-navy">{pounds(l.line_total_pence)}</span>
                    </div>
                  ))}
                  <div className="flex justify-between text-[10px] pt-2 text-gray-500">
                    <span>Subtotal {pounds(detail.subtotal_pence)} · VAT {pounds(detail.vat_pence)}</span>
                    <span className="font-extrabold text-navy">Total {pounds(detail.total_pence)}</span>
                  </div>
                  {detail.notes && <div className="mt-2 text-[9px] text-gray-500 bg-amber-50 border border-amber-100 rounded p-2">Order notes: {detail.notes}</div>}

                  <div className="flex gap-2 mt-3 flex-wrap">
                    {detail.status === 'submitted' && (
                      <>
                        <button disabled={busy} onClick={() => setStatus(o.id, 'accepted', 'Accepted and the client notified')}
                          className="btn-primary rounded px-3 py-1.5 text-[10px] font-bold">Accept</button>
                        <button disabled={busy} onClick={() => window.confirm(`Cancel ${o.reference}? The client will be emailed.`) && setStatus(o.id, 'cancelled', 'Cancelled and the client notified')}
                          className="btn-secondary border rounded px-3 py-1.5 text-[10px] font-bold">Cancel</button>
                      </>
                    )}
                    {detail.status === 'accepted' && (
                      <>
                        <button disabled={busy} onClick={() => raisePo(o.id)}
                          className="btn-primary rounded px-3 py-1.5 text-[10px] font-bold">Raise supplier PO</button>
                        <button disabled={busy} onClick={() => window.confirm(`Cancel ${o.reference}? The client will be emailed.`) && setStatus(o.id, 'cancelled', 'Cancelled and the client notified')}
                          className="btn-secondary border rounded px-3 py-1.5 text-[10px] font-bold">Cancel</button>
                      </>
                    )}
                    {detail.status === 'ordered' && (
                      <button disabled={busy} onClick={() => setStatus(o.id, 'delivered', 'Marked delivered and the client notified')}
                        className="btn-primary rounded px-3 py-1.5 text-[10px] font-bold">Mark delivered</button>
                    )}
                    {detail.po_id && (
                      <button onClick={() => navigate('pos', detail.po_id)}
                        className="btn-secondary border rounded px-3 py-1.5 text-[10px] font-bold">View PO</button>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
        {!loading && visible.length === 0 && (
          <div className="text-center py-8 text-[10px] text-gray-400">No client orders{filter !== 'all' ? ` with status ${filter}` : ''}.</div>
        )}
      </div>
    </div>
  );
}
